import type {
  FilterClause, MarketSurvey, ShownColumns, ShownField, SurveyColumn,
} from '../types'
import { SHOWN_FIELDS } from '../types'

/**
 * The fields the table shows, and what each one is called on screen.
 *
 * **The same handful for every survey.** One provider's file has eleven
 * columns and the next has twenty two, and a table drawn from whatever each
 * file happened to carry gave a reader a different shape per survey and no
 * way to read down a column across two of them.
 *
 * **These are the fields the rest of the product already reads.** Which
 * column holds each one is resolved on the server, by the same functions the
 * benchmarking index and the matcher use, and sent with the survey. A screen
 * reading a different column from the one the agent reasons over lets a
 * figure look right and be matched on something else.
 *
 * **Nothing is dropped.** Every column the provider published is still
 * stored, still searched, still indexed, and still on the record's own
 * screen, which is where somebody who wants all twenty two goes.
 */
export const FIELD_LABEL: Record<ShownField, string> = {
  code: 'Job code',
  title: 'Job title',
  family: 'Job family',
  level: 'Level',
  country: 'Country',
  incumbents: 'Incumbents',
  orgs: 'Organisations',
  // The measure these belong to is said once above the table, because it is
  // the same for every row and the column titles are long enough already.
  p25: 'Base P25',
  p50: 'Base P50',
  p75: 'Base P75',
  p90: 'Base P90',
  cash25: 'Total cash P25',
  cash50: 'Total cash P50',
  cash75: 'Total cash P75',
  equity25: 'Equity P25',
  equity50: 'Equity P50',
  equity75: 'Equity P75',
}

/** Which way a column is read, which decides how its cells are set. */
export const FIELD_KIND: Record<ShownField, SurveyColumn['kind']> = {
  code: 'text', title: 'text', family: 'text', level: 'text', country: 'text',
  incumbents: 'number', orgs: 'number',
  p25: 'money', p50: 'money', p75: 'money', p90: 'money',
  cash25: 'money', cash50: 'money', cash75: 'money',
  equity25: 'money', equity50: 'money', equity75: 'money',
}

/** Where one field sits in one survey, or nowhere when that file does not carry it. */
export const keyOf = (
  survey: MarketSurvey | undefined, field: ShownField,
): string | undefined => (survey?.shown as ShownColumns | undefined)?.[field]

/**
 * A field that says the same thing on every row, and the value it says.
 *
 * A survey of eight hundred rows from one country prints that country eight
 * hundred times. The column carries no information and it takes width from
 * the figures somebody came for.
 */
export interface Constant {
  field: ShownField
  value: string
}

/**
 * The one value a column holds across a whole survey, or nothing.
 *
 * **Across the survey, not across the page.** `survey.filters` carries each
 * column's distinct values, worked out once while the file was read, so this
 * gives the same answer on every page. Judging it from the rows on screen
 * would make a column appear on page two and vanish on page three, which is
 * worse than the repetition it removes.
 *
 * A column with `tooMany` values is never constant: that flag means the list
 * was abandoned for being long, which is the opposite of one value.
 */
export function onlyValue(
  survey: MarketSurvey | undefined, key: string | undefined,
): string | undefined {
  if (!survey || !key) return undefined
  const filter = (survey.filters ?? []).find((f) => f.key === key)
  if (!filter || filter.tooMany || filter.values.length !== 1) return undefined
  // A column whose one value is blank says even less than one that repeats a
  // word, and an empty figure above the table reads as a fault.
  const only = String(filter.values[0] ?? '').trim()
  return only === '' ? undefined : only
}

/**
 * Which fields the table draws, and which it says once above itself instead.
 *
 * Two kinds of column come out.
 *
 * **A field no survey on screen carries at all.** It would be blank on every
 * row, and three columns of nothing is what pushed the job title behind the
 * frozen column on a real Mercer file. A column with no data in it anywhere
 * is not a column, and it is not information about the file either: the
 * record's own screen already shows exactly what the provider published.
 *
 * **A field holding one value across every survey on screen.** It is said
 * once above the table instead, because eight hundred rows all reading
 * "United States" take width from the figures somebody came for. Counted
 * only where **every** survey with rows on screen holds one value for it and
 * it is the same value: one survey's United States beside another's Germany
 * is the difference between two rows, which is exactly what the table is for.
 *
 * **Never a figure, for the second rule.** A pay column holding one value is
 * a fact about that survey worth seeing next to the other figures. The
 * repetition this removes is a label repeated, not a number. A pay column no
 * survey carries still goes, because it has nothing in it at all.
 */
export function foldFields(
  surveys: MarketSurvey[],
  present: Set<string>,
): { shown: ShownField[]; constants: Constant[] } {
  const all = [...SHOWN_FIELDS]
  const inView = surveys.filter((s) => present.has(s.id))
  if (inView.length === 0) return { shown: all, constants: [] }

  const constants: Constant[] = []
  const missing = new Set<ShownField>()
  const constant = new Set<ShownField>()

  for (const field of all) {
    // Nobody carries it, so there is nothing to draw and nothing to say.
    if (!inView.some((s) => keyOf(s, field))) {
      missing.add(field)
      continue
    }
    if (FIELD_KIND[field] !== 'text') continue
    const values = inView.map((s) => onlyValue(s, keyOf(s, field)))
    if (values.some((v) => v === undefined)) continue
    const only = values[0]!
    if (values.some((v) => v !== only)) continue
    constant.add(field)
    constants.push({ field, value: only })
  }

  const carried = all.filter((f) => !missing.has(f))
  const shown = carried.filter((f) => !constant.has(f))

  /*
   * A file carrying one field, which holds one value, would otherwise draw
   * no table at all. The line above a table is not a table, so in that case
   * the constants stay where they are and say themselves in place.
   */
  if (shown.length === 0) return { shown: carried, constants: [] }
  return { shown, constants }
}

/**
 * What to call each survey in the table's first column.
 *
 * "Mercer 2026" is not a name when the company holds two of them, and that
 * label is the only thing on the row saying where its figures came from.
 * Where a provider and year are shared, the file is named underneath. Where
 * they are not, the file would be noise.
 */
export function surveyLabels(
  surveys: MarketSurvey[],
): (id: string) => { title: string; file?: string } {
  const times = new Map<string, number>()
  for (const s of surveys) {
    const title = `${s.provider} ${s.year}`
    times.set(title, (times.get(title) ?? 0) + 1)
  }
  const m = new Map(
    surveys.map((s) => {
      const title = `${s.provider} ${s.year}`
      const shared = (times.get(title) ?? 0) > 1
      return [s.id, { title, file: shared ? s.files?.[0]?.fileName : undefined }] as const
    }),
  )
  return (id: string) => m.get(id) ?? { title: id }
}

/**
 * How a set of ticks is asked for, which depends on which side is shorter.
 *
 * Unticking one value out of four hundred leaves three hundred and ninety nine
 * ticked. Asking for all of them by name is far past the size of question the
 * database accepts, and it does not answer slowly, it refuses: the screen said
 * "that did not load" and gave no clue which tick had caused it. Naming the
 * one that was unticked asks the same thing in a few characters.
 *
 * Everything ticked is no filter at all, so it asks for nothing.
 */
export function sideOf(ticked: string[], all: string[], keys: string[]): FilterClause | null {
  if (!ticked.length || ticked.length === all.length) return null
  const kept = new Set(ticked)
  const dropped = all.filter((v) => !kept.has(v))
  return dropped.length < ticked.length ? { keys, none: dropped } : { keys, any: ticked }
}

/**
 * What a record is called, which is the job it is about.
 *
 * The screen for one record used to be headed with its survey, so all eight
 * hundred records in a file were headed `Mercer 2026` and the row somebody
 * had clicked was the second box down the page.
 *
 * **Read from the plain English name the admin confirmed, not the provider's
 * heading.** `MjlCode` means nothing; "Job code" is what a person agreed it
 * was, and that correction is the authority. This is the same order the
 * matcher's own `pickColumns` reads them in, and the two are meant to agree.
 *
 * Falls back to the code, and then to nothing, so a caller can say the survey
 * rather than this inventing a name for a row that has none.
 */
export function recordName(
  values: Record<string, string>,
  columns: SurveyColumn[],
): string | null {
  const named = (patterns: RegExp[]): string | null => {
    for (const pattern of patterns) {
      for (const col of columns) {
        if (col.kind !== 'text') continue
        if (!pattern.test(String(col.name ?? ''))) continue
        const value = String(values[col.key] ?? '').trim()
        if (value !== '') return value
      }
    }
    return null
  }
  // "Job title" before a bare "title", so a survey carrying both does not
  // pick the report's title by accident.
  return named([/\bjob title\b/i, /\bposition title\b/i, /\btitle\b/i, /\bjob name\b/i])
    ?? named([/\bjob code\b/i, /\bcode\b/i])
}
